import type {
	CatalogProduct,
	MultimediaAsset,
	ProductImage
} from "@/domain/catalog";
import type { ProductGallerySlide } from "@/composites/product/ProductGallery";
import { extractWidenAssetId } from "./extract-widen-asset-id";

/**
 * Maps `CatalogProduct` imagery to the flat slide list `ProductGallery` consumes.
 * Order: lifestyle → silo → mediaGallery.
 *
 */
export function toGallerySlides(
	product: CatalogProduct
): ProductGallerySlide[] {
	const slides: ProductGallerySlide[] = [];

	const lifestyle = pickProductImage(product.lifestyleImage);
	if (lifestyle) slides.push(lifestyle);

	const silo = pickProductImage(product.heroImage);
	if (silo) slides.push(silo);

	for (const asset of product.mediaGallery ?? []) {
		const slide = pickMultimediaAsset(asset);
		if (slide) slides.push(slide);
	}

	return slides;
}

function pickProductImage(
	image: ProductImage | undefined
): ProductGallerySlide | null {
	if (!image?.url) return null;
	return {
		src: image.url,
		alt: image.altText || "Product image",
		kind: "image",
		assetId: extractWidenAssetId(image.url)
	};
}

function pickMultimediaAsset(
	asset: MultimediaAsset
): ProductGallerySlide | null {
	if (!asset?.url) return null;
	if (asset.type === "image") {
		return {
			src: asset.url,
			alt: asset.altText || "Product image",
			kind: "image",
			assetId: extractWidenAssetId(asset.url)
		};
	}
	// video
	return {
		src: toPlayableVideoUrl(asset.url),
		alt: asset.altText || "Product video",
		kind: "video",
		poster: asset.posterUrl || undefined,
		assetId: extractWidenAssetId(asset.url)
	};
}

function toPlayableVideoUrl(url: string): string {
	try {
		const u = new URL(url);
		if (!u.hostname.endsWith("widen.net")) return url;
		const match = u.pathname.match(/^\/view\/video\/([^/]+)\/(.+)$/);
		if (!match) return url;
		u.pathname = `/content/${match[1]}/mp4/${match[2]}`;
		return u.toString();
	} catch {
		return url;
	}
}
